const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

async function fetchApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `API error: ${response.status}`);
  }

  return response.json();
}

export const api = {
  // Dashboard stats
  getDashboardStats: () => fetchApi<DashboardStats>('/stats/dashboard'),

  // Entities
  searchEntities: (params: EntitySearchParams) => {
    const searchParams = new URLSearchParams();
    if (params.name) searchParams.set('name', params.name);
    if (params.dob) searchParams.set('dob', params.dob);
    if (params.email) searchParams.set('email', params.email);
    if (params.state) searchParams.set('state', params.state);
    return fetchApi<{ entities: EntitySummary[] }>(`/entities/search?${searchParams}`);
  },

  getEntity: (entityId: number) => fetchApi<EntityDetail>(`/entities/${entityId}`),

  getEntityWhy: (entityId: number) => fetchApi<WhyAnalysis>(`/entities/${entityId}/why`),

  // List entities with filtering
  listEntities: (params?: {
    page?: number;
    limit?: number;
    sortBy?: 'entityId' | 'name' | 'lastActivity';
    sortDir?: 'asc' | 'desc';
    dapOnly?: boolean;
    dataSource?: string;
    matchLevel?: 'possible' | 'possibly_related' | 'resolved';
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortDir) searchParams.set('sortDir', params.sortDir);
    if (params?.dapOnly) searchParams.set('dapOnly', 'true');
    if (params?.dataSource) searchParams.set('dataSource', params.dataSource);
    if (params?.matchLevel) searchParams.set('matchLevel', params.matchLevel);
    if (params?.search) searchParams.set('search', params.search);
    return fetchApi<EntityListResult>(`/entities/list?${searchParams}`);
  },

  // Ingestion
  uploadFile: async (file: File, dataSource: string, mappingConfig: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dataSource', dataSource);
    formData.append('mappingConfig', mappingConfig);

    const response = await fetch(`${API_BASE_URL}/api/v1/ingestion/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  },

  getIngestionJobs: (params?: { status?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<{ jobs: IngestionJob[]; pagination: Pagination }>(`/ingestion/jobs?${searchParams}`);
  },

  getIngestionJob: (jobId: string) => fetchApi<IngestionJobDetail>(`/ingestion/jobs/${jobId}`),

  startIngestionJob: (jobId: string) => fetchApi<{ status: string }>(`/ingestion/jobs/${jobId}/start`, { method: 'POST', body: JSON.stringify({}) }),

  // Conflicts
  getConflicts: (params?: ConflictSearchParams) => {
    const searchParams = new URLSearchParams();
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.jurisdiction) searchParams.set('jurisdiction', params.jurisdiction);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<ConflictSearchResult>(`/conflicts?${searchParams}`);
  },

  getConflictStats: () => fetchApi<ConflictStats>('/conflicts/stats'),

  // Resolve a conflict (mark as false positive)
  resolveConflict: (entityId: number, notes?: string) =>
    fetchApi<{ success: boolean }>(`/conflicts/${entityId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  // Data Sources
  getDataSources: () => fetchApi<{ dataSources: DataSource[]; senzingDataSources: string[] }>('/data-sources'),

  getDataSourceStats: (code: string) => fetchApi<DataSourceStats>(`/data-sources/${code}/stats`),

  // Possible Matches
  getPossibleMatches: (params?: PossibleMatchParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.matchKey) searchParams.set('matchKey', params.matchKey);
    if (params?.search) searchParams.set('search', params.search);
    return fetchApi<PossibleMatchResult>(`/possible-matches?${searchParams}`);
  },

  getPossibleMatchStats: () => fetchApi<PossibleMatchStats>('/possible-matches/stats'),

  getWhyEntities: (entityId1: number, entityId2: number) =>
    fetchApi<WhyEntitiesResult>(`/possible-matches/why/${entityId1}/${entityId2}`),

  // Adjudication
  getAdjudicationQueue: (params?: { status?: string; limit?: number; dapOnly?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.dapOnly) searchParams.set('dapOnly', 'true');
    return fetchApi<AdjudicationQueueResult>(`/adjudication?${searchParams}`);
  },

  getAdjudicationStats: () => fetchApi<AdjudicationStats>('/adjudication/stats'),

  getAdjudicationItem: (id: string) => fetchApi<AdjudicationItemDetail>(`/adjudication/${id}`),

  approveAdjudication: (id: string, decidedBy?: string) =>
    fetchApi<{ success: boolean }>(`/adjudication/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ decidedBy: decidedBy || 'user' }),
    }),

  rejectAdjudication: (id: string, reason: string, decidedBy?: string) =>
    fetchApi<{ success: boolean }>(`/adjudication/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ decidedBy: decidedBy || 'user', reason }),
    }),

  skipAdjudication: (id: string, decidedBy?: string) =>
    fetchApi<{ success: boolean }>(`/adjudication/${id}/skip`, {
      method: 'POST',
      body: JSON.stringify({ decidedBy: decidedBy || 'user' }),
    }),

  createAdjudicationItem: (entityId1: number, entityId2: number, matchKey?: string) =>
    fetchApi<{ success: boolean; item: AdjudicationItem }>('/adjudication/create', {
      method: 'POST',
      body: JSON.stringify({
        entityId1,
        entityId2,
        matchType: 'POSSIBLE_MATCH',
        matchConfidence: matchKey?.includes('+NAME') && matchKey?.includes('+DOB') ? 85 : 70,
      }),
    }),

  // Get merge history for an entity
  getEntityMergeHistory: (entityId: number) =>
    fetchApi<{ merges: Array<{ id: string; mergedEntityId: number; mergedIntoEntityId: number; decidedBy: string; decidedAt: string; notes: string | null }> }>(
      `/adjudication/history/${entityId}`
    ),

  // Confirm DAP status for an entity (set IS_DAP_FLAG = '01')
  confirmDap: (entityId: number, confirmedBy?: string) =>
    fetchApi<{ success: boolean; entityId: number; updated: number; message: string }>(
      `/adjudication/confirm-dap/${entityId}`,
      {
        method: 'POST',
        body: JSON.stringify({ confirmedBy: confirmedBy || 'user' }),
      }
    ),
};

// Types
export interface DashboardStats {
  summary: {
    totalRecords: number;
    totalEntities: number;
    duplicateRate: number;
    activeConflicts: number;
    duplicatesFound: number;
  };
  recordsBySource: Record<string, number>;
  recentJobs: Array<{
    id: string;
    filename: string;
    dataSource: string;
    status: string;
    recordCount: number;
    successCount: number;
    errorCount: number;
    createdAt: string;
    completedAt: string | null;
  }>;
  systemStatus: {
    senzingApiHealthy: boolean;
    databaseConnected: boolean;
  };
}

export interface EntitySearchParams {
  name?: string;
  dob?: string;
  email?: string;
  state?: string;
}

export interface EntitySummary {
  entityId: number;
  resolvedName: string;
  matchType: string;
  recordCount: number;
  dataSources: string[];
  bestName: string;
  bestAddress: string | null;
  bestDOB: string | null;
  confidenceScore: number;
}

export interface EntityListItem {
  entityId: number;
  name: string;
  email: string | null;
  matchLevel: string;
  matchKey: string | null;
  dataSources: string[];
  hasDap: boolean;
  isDapConfirmed: boolean;
  recordCount: number;
  lastActivity: string;
  status: 'active' | 'pending' | 'reviewed';
}

export interface EntityListResult {
  entities: EntityListItem[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface EntityDetail {
  entityId: number;
  resolvedEntity: {
    bestName: string;
    allNames: string[];
    bestAddress: { line1?: string; city?: string; state?: string; postalCode?: string } | null;
    allAddresses: Array<{ line1?: string; city?: string; state?: string; postalCode?: string }>;
    bestDOB: string | null;
    emails: string[];
    phones: string[];
    recordCount: number;
    dataSources: string[];
  };
  linkedRecords: Array<{
    dataSource: string;
    recordId: string;
    matchScore: number;
    matchKey: string;
    rawData: Record<string, unknown>;
    ingestedAt: string;
  }>;
  possibleMatches: Array<{
    entityId: number;
    matchLevel: number;
    matchKey?: string;
    resolvedName: string;
    dataSources: string[];
  }>;
  possibleRelationships: Array<{
    entityId: number;
    matchLevel: number;
    matchKey?: string;
    resolvedName: string;
    dataSources: string[];
  }>;
}

export interface WhyAnalysis {
  entityId: number;
  whyResults: Array<{
    internalId: number;
    matchInfo: {
      whyKey: string;
      matchLevel: number;
      matchLevelCode: string;
      featureScores: Record<string, { inboundFeature: string; candidateFeature: string; score: number }>;
    };
  }>;
}

export interface IngestionJob {
  id: string;
  filename: string;
  originalFilename: string;
  dataSource: string;
  mappingConfig: string;
  status: string;
  totalRecords: number | null;
  processedRecords: number;
  successCount: number;
  errorCount: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface IngestionJobDetail extends IngestionJob {
  progress: {
    totalRecords: number;
    processedRecords: number;
    successCount: number;
    errorCount: number;
    percentComplete: number;
  } | null;
  errorLog: Array<{
    recordId: string;
    lineNumber: number;
    error: string;
    timestamp: string;
  }> | null;
}

export interface Pagination {
  page?: number;
  limit?: number;
  offset?: number;
  total: number;
  totalCount?: number;
  totalPages?: number;
}

export interface ConflictSearchParams {
  severity?: string;
  jurisdiction?: string;
  page?: number;
  limit?: number;
}

export interface Conflict {
  id: string;
  entityId: number;
  resolvedName: string;
  loyaltyRecordCount: number;
  exclusionRecordCount: number;
  loyaltyDataSources: string[];
  exclusionDataSources: string[];
  matchConfidence: number;
  jurisdictions: string[];
  severity: string;
  detectedAt: string;
  isResolved: boolean;
  resolvedAt?: string | null;
  resolutionNotes?: string | null;
  isDapAlert?: boolean;
  exclusionReason?: string | null;
  exclusionDate?: string | null;
}

export interface ConflictStats {
  totalConflicts: number;
  highSeverity: number;
  mediumSeverity: number;
  lowSeverity: number;
  byJurisdiction: Record<string, number>;
  byDataSource: Record<string, number>;
}

export interface ConflictSearchResult {
  conflicts: Conflict[];
  summary: ConflictStats;
  pagination: Pagination;
}

export interface DataSource {
  code: string;
  displayName: string;
  description: string | null;
  sourceType: string;
  jurisdiction: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface DataSourceStats {
  code: string;
  displayName: string;
  recordCount: number;
  entityCount: number;
  lastIngestionAt: string | null;
  duplicateRate: number;
}

export interface PossibleMatchParams {
  page?: number;
  limit?: number;
  matchKey?: string;
  search?: string;
}

export interface PossibleMatchPair {
  entityId: number;
  entityName: string | null;
  entityRecordCount: number;
  relatedEntityId: number;
  relatedEntityName: string | null;
  relatedRecordCount: number;
  matchKey: string | null;
  matchType: string;
}

export interface PossibleMatchResult {
  possibleMatches: PossibleMatchPair[];
  pagination: Pagination;
}

export interface PossibleMatchStats {
  totalEntitiesWithPossibleMatches: number;
  totalPossibleMatchPairs: number;
  matchKeyDistribution: Record<string, number>;
}

export interface WhyEntitiesResult {
  entityId1: number;
  entityId2: number;
  entity1Name: string;
  entity2Name: string;
  matchInfo: {
    whyKey: string;
    matchLevel: string;
    resolutionRule: string;
  };
  featureComparison: Array<{
    featureType: string;
    entity1Value: string | null;
    entity2Value: string | null;
    score: number;
    scoringBucket: string;
  }>;
  entity1Record: {
    dataSource: string;
    recordId: string;
    rawData: Record<string, unknown>;
  } | null;
  entity2Record: {
    dataSource: string;
    recordId: string;
    rawData: Record<string, unknown>;
  } | null;
}

export interface AdjudicationItem {
  id: string;
  entityId1: number;
  entityId2: number;
  matchType: string | null;
  matchConfidence: number | null;
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  decision: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  notes: string | null;
}

export interface AdjudicationQueueResult {
  items: AdjudicationItem[];
  pagination: Pagination;
}

export interface AdjudicationStats {
  pending: number;
  approved: number;
  rejected: number;
  skipped: number;
  total: number;
  avgConfidence: number;
}

export interface LinkedRecord {
  dataSource: string;
  recordId: string;
  matchKey: string;
  rawData: Record<string, unknown>;
}

export interface EntityWithRecords extends EntitySummary {
  linkedRecords?: LinkedRecord[];
}

export interface AdjudicationItemDetail {
  item: AdjudicationItem;
  entity1: EntityWithRecords | null;
  entity2: EntityWithRecords | null;
}
